import { useEffect, useState, FormEvent } from 'react';
import type { TeamRole } from '@mockd/shared';
import { useTeamInvites } from '../../hooks';
import { Loading } from '../common';

type InvitableRole = Exclude<TeamRole, 'owner'>;
const ROLES: InvitableRole[] = ['admin', 'member', 'viewer'];

interface TeamInvitesManagerProps {
  teamId: string;
  seatLimit: number;
  // Members plus pending invites have filled every seat; the server would reject a send.
  atSeatLimit: boolean;
  // Called after a send or revoke, so the parent can refresh its seat counts.
  onChange?: () => void;
}

export function TeamInvitesManager({ teamId, seatLimit, atSeatLimit, onChange }: TeamInvitesManagerProps) {
  const { invites, loading, fetchInvites, sendInvite, revokeInvite } = useTeamInvites();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>('member');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (teamId) fetchInvites(teamId);
  }, [teamId, fetchInvites]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || atSeatLimit) return;
    setSending(true);
    try {
      await sendInvite(teamId, { email: email.trim(), role });
      setEmail('');
      setRole('member');
      onChange?.();
    } catch {
      // toast handled in the hook
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    try {
      await revokeInvite(teamId, inviteId);
      onChange?.();
    } catch {
      // toast handled in the hook
    }
  };

  return (
    <div className="space-y-6">
      {atSeatLimit && (
        <p className="text-sm text-base-content/70">
          All {seatLimit} seats are in use — revoke an invite or remove a member to free one.
        </p>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="fieldset flex-1">
          <label htmlFor="invite-email" className="fieldset-legend">
            Email address
          </label>
          <input
            id="invite-email"
            type="email"
            required
            disabled={atSeatLimit}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="input w-full"
          />
        </div>
        <div className="fieldset">
          <label htmlFor="invite-role" className="fieldset-legend">
            Role
          </label>
          <select
            id="invite-role"
            value={role}
            disabled={atSeatLimit}
            onChange={(e) => setRole(e.target.value as InvitableRole)}
            className="select"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn btn-primary" disabled={sending || atSeatLimit}>
          {sending ? 'Sending…' : 'Send invite'}
        </button>
      </form>

      <div>
        <h3 className="mb-2">Pending invites</h3>
        {loading ? (
          <Loading className="py-6" />
        ) : invites.length === 0 ? (
          <p className="text-sm text-base-content/60 py-4">No pending invites.</p>
        ) : (
          <ul className="divide-y divide-base-300 border border-base-300 rounded-box">
            {invites.map((invite) => (
              <li key={invite.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{invite.email}</p>
                  <p className="text-xs text-base-content/60">
                    {invite.role} · expires {new Date(invite.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  className="btn btn-ghost btn-sm text-error"
                  onClick={() => handleRevoke(invite.id)}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
